import { useBackendQuery as useQuery } from "@/hooks/useBackendQuery";
import { HotkeyDecorator } from "@/components/atoms/HotkeyDecorator";

import { IObjectWithTypename } from "@/components/@types";
import { NodeLayout } from "@/components/organisms/Node/NodeLayout";
import { NodeTitleLayout } from "@/components/organisms/Node/NodeTitleLayout";
import { useNode, useNodeSetup } from "@/components/organisms/Node/Node.hooks";
import { StickyLayout } from "@/components/organisms/StickyLayout";
import { Queries } from "@/constants";
import { CommandCenterContextEntityPicker } from "@/layouts/AppLayout/CommandCenter/CommandCenterContextEntityPicker";
import { idToTypename, typenameToHumanName } from "@/objects/@utils";
import { State } from "@/state";
import { Button } from "@/components/ui/button";
import { useEffect, useLayoutEffect, useRef } from "react";

export const COMMAND_ID_PREFIX = "update-";

export type UpdateNodeContextType = {
  nodeId?: string;
  entityIdentifier?: string;
  /** The tab it opens on - a document's Preview, say - rather than the last one seen. */
  initialGroupId?: string;
  /**
   * What it was opened to decide, in its footer beside Close and by ⌘↵ (Ctrl
   * elsewhere): a suggested match, booked from the document it shows.
   */
  action?: { label: string; onPress: () => void };
};

/**
 * Close, and the action it was opened for - by its button, or by ⌘↵ (Ctrl+↵
 * elsewhere), which the dialog gives the one marked to submit it; the record's
 * own Enter is left to its fields. Drawn only when opened for one
 * (``hasFooter``).
 */
function Footer({
  action,
}: {
  action: NonNullable<UpdateNodeContextType["action"]>;
}) {
  return (
    <div className="flex gap-2 items-center">
      <Button
        variant="outline"
        data-testid="update-node-close"
        onClick={() => State.getState().popCommandsFromStack()}
      >
        Close
        <HotkeyDecorator hotkey="escape" color="inherit" />
      </Button>
      <Button
        data-testid="update-node-action"
        data-context-overlay-submit=""
        onClick={() => {
          State.getState().popCommandsFromStack();
          action.onPress();
        }}
      >
        {action.label}
        <HotkeyDecorator
          hotkey={{
            mac: "meta+enter",
            windows: "ctrl+enter",
            linux: "ctrl+enter",
          }}
          color="inherit"
        />
      </Button>
    </div>
  );
}

export function Component({
  context,
}: {
  commandInstanceId: string;
  commandDefinitionId: string;
  context?: UpdateNodeContextType;
}) {
  const nodeId = context?.nodeId;
  const typename = idToTypename(nodeId);
  const dialogLayoutMode = State((state) => state.dialogLayoutMode);

  const { data, loading: isLoading } = useQuery(Queries.NODE_QUERY, {
    variables: nodeId
      ? {
          id: nodeId,
          startDate: State.getState().timeSeriesFilter.startDate?.toISOString(),
          endDate: State.getState().timeSeriesFilter.endDate?.toISOString(),
        }
      : undefined,
    skip: !nodeId,
  });

  const { store } = useNodeSetup({
    mode: "update",
    node: data?.node as IObjectWithTypename,
    typename: typename,
  });
  const { actions, metadata } = useNode(store);
  // Opened, the keyboard is on the record, not the dialog's first control -
  // which, on a tab with no field to take it (a document's preview), left
  // Split view looking picked. A tab with fields still puts it on the first.
  const contentRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    contentRef.current?.focus({ preventScroll: true });
  }, []);

  // Map dialogLayoutMode to NodeLayout's forceLayoutMode
  const forceLayoutMode = dialogLayoutMode === "split" ? "cards" : "tabs";

  return (
    <StickyLayout
      stickyTopChildren={
        <NodeTitleLayout
          title={metadata.title}
          isLoading={isLoading}
          store={store}
          className="bg-popover"
          actions={actions}
        />
      }
      childrenClassName="flex-col max-w-[calc(100%)] w-[calc(100%)] h-[calc(100%)] overflow-y-auto overflow-x-hidden relative items-stretch flex-nowrap pt-0 pb-0"
    >
      <div
        ref={contentRef}
        tabIndex={-1}
        // As tall as where it is, no taller: the record scrolls itself under
        // its title and tabs, which a box grown to its content left scrolling
        // with it, over the dialog's own top bar.
        className="[outline:none] w-full h-full min-h-0 flex flex-col"
      >
        <NodeLayout
          autofocus={true}
          store={store}
          isLoading={isLoading}
          isSingleClick={true}
          forceLayoutMode={forceLayoutMode}
          initialGroupId={context?.initialGroupId}
        />
      </div>
    </StickyLayout>
  );
}

export function UpdateNodeCommand({ typename }: { typename: string }) {
  const registerCommandDefinition = State(
    (state) => state.registerCommandDefinition,
  );
  const unregisterCommandDefinition = State(
    (state) => state.unregisterCommandDefinition,
  );

  useLayoutEffect(() => {
    registerCommandDefinition({
      id: `${COMMAND_ID_PREFIX}${typename}`,
      title: "Update Node",
      description: "Update the node",
      component: Component,
      footer: ({ context }) => {
        const action = (context as UpdateNodeContextType | undefined)?.action;
        return action ? <Footer action={action} /> : null;
      },
      hasFooter: (context) =>
        Boolean((context as UpdateNodeContextType | undefined)?.action),
      context: ({ commandInstanceId, context }) => {
        const updateNodeContext = context as UpdateNodeContextType | undefined;
        return (
          <CommandCenterContextEntityPicker
            entityIdentifier={updateNodeContext?.entityIdentifier}
          />
        );
      },
      getLabel: () => typenameToHumanName(typename),
      isHidden: true,
      sizes: ["sm", "md", "lg"],
    });

    return () => {
      unregisterCommandDefinition(`${COMMAND_ID_PREFIX}${typename}`);
    };
  }, [typename]);

  return null;
}
